import {
  JobServiceSubscriptionMessage,
  JobServiceSubscriptionMessage_Type as JobServiceSubscriptionMessageType,
  JobServiceSubscriptionRequest,
  JobServiceSubscriptionRequest_Target as JobServiceSubscriptionRequestTarget,
  WebSocketAuthenticateRequest,
  WebSocketResponse,
  WebSocketStatus,
} from "protos/rtc/web_socket";
import { jobWebsocketMiddlewareInstance } from "common/state/jobWebsocket/jobWebsocketMiddleware";
import { QueryEnv } from "common/state/queryEnv";
import { SubscriptionManagerContext } from "common/components/WebSocketProvider";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSharedWebsocket } from "common/hooks/useSharedWebsocket";
import { useStore } from "react-redux";

const isSubscriptionMessage = (m: any): m is JobServiceSubscriptionMessage =>
  ("type" in m && m.type in JobServiceSubscriptionMessageType) ||
  ("activeObjective" in m && m.activeObjective !== undefined) ||
  ("activeAtomicTask" in m && m.activeAtomicTask !== undefined) ||
  ("job" in m && m.job !== undefined) ||
  ("objective" in m && m.objective !== undefined) ||
  ("task" in m && m.task !== undefined) ||
  ("intervention" in m && m.intervention !== undefined) ||
  ("objectiveAssignment" in m && m.objectiveAssignment !== undefined) ||
  ("jobConfig" in m && m.jobConfig !== undefined);

const isWebSocketResponse = (m: any): m is WebSocketResponse =>
  "status" in m && "message" in m;

interface UseJobWebsocketReturn {
  authenticated: boolean;
  subscribe: (
    identifier: string,
    target: JobServiceSubscriptionRequestTarget,
    type: JobServiceSubscriptionMessageType,
    callback?: (message: JobServiceSubscriptionMessage) => void
  ) => void;
  unsubscribe: (
    identifier: string,
    target: JobServiceSubscriptionRequestTarget,
    type: JobServiceSubscriptionMessageType,
    callback?: (message: JobServiceSubscriptionMessage) => void
  ) => void;
}

export const useJobWebsocket = (): UseJobWebsocketReturn => {
  const subscriptionManager = useContext(SubscriptionManagerContext);
  const { getState } = useStore();
  const listenerIdRef = useRef<string>(Math.random().toString(36).slice(2, 11));
  const [authenticated, setAuthenticated] = useState<boolean>(
    subscriptionManager.isAuthenticated()
  );

  const onMessage = useCallback(
    (m: JobServiceSubscriptionMessage | WebSocketResponse) => {
      if (isWebSocketResponse(m)) {
        const message = WebSocketResponse.fromJSON(m);
        if (
          message.status === WebSocketStatus.OK &&
          message.message === "authenticated"
        ) {
          subscriptionManager.setAuthenticated(true);
          subscriptionManager.setAuthenticating(false);
          jobWebsocketMiddlewareInstance.setAuthenticated(true);
          // need to report back to the ui but subscription manager is the source of truth
          setAuthenticated(true);
        }
      } else if (isSubscriptionMessage(m)) {
        const message = JobServiceSubscriptionMessage.fromJSON(m);
        subscriptionManager.handleMessage(message);
        jobWebsocketMiddlewareInstance.handleMessage(message);
      }
    },
    [subscriptionManager]
  );

  const { sendMessage, isConnected } = useSharedWebsocket<
    WebSocketAuthenticateRequest | JobServiceSubscriptionRequest,
    JobServiceSubscriptionMessage | WebSocketResponse
  >(`${QueryEnv.get().rtcJobsBaseUrl}/ws/v1/event-subscription`, {
    onMessage,
  });

  useEffect(() => {
    subscriptionManager.setSendMessage(sendMessage);
    jobWebsocketMiddlewareInstance.setSendMessage(sendMessage);
  }, [subscriptionManager, sendMessage]);

  const handleAuthentication = useCallback(async () => {
    const audience = QueryEnv.get().auth0Audience;
    const token = await QueryEnv.get().accessTokenProvider({ getState });
    if (token) {
      subscriptionManager.authenticate(audience, token);
    } else {
      subscriptionManager.reset();
    }
  }, [getState, subscriptionManager]);

  const subscribe = useCallback(
    (
      identifier: string,
      target: JobServiceSubscriptionRequestTarget,
      type: JobServiceSubscriptionMessageType,
      callback?: (message: JobServiceSubscriptionMessage) => void
    ) => {
      const listenerId = listenerIdRef.current;
      subscriptionManager.subscribe(
        identifier,
        target,
        type,
        listenerId,
        callback ??
          ((m) => {
            console.warn("message", m);
          })
      );
    },
    [subscriptionManager]
  );

  const unsubscribe = useCallback(
    (
      identifier: string,
      target: JobServiceSubscriptionRequestTarget,
      type: JobServiceSubscriptionMessageType,
      _?: (message: JobServiceSubscriptionMessage) => void
    ) => {
      const listenerId = listenerIdRef.current;
      subscriptionManager.unsubscribe(identifier, target, type, listenerId);
    },
    [subscriptionManager]
  );

  // when the component unmounts we should remove this client from all their subscriptions
  useEffect(() => {
    const listenerId = listenerIdRef.current;
    return () => {
      subscriptionManager.unsubscribeAll(listenerId);
    };
  }, [subscriptionManager]);

  if (
    isConnected &&
    !subscriptionManager.isAuthenticating() &&
    (subscriptionManager.needsReauth() ||
      !subscriptionManager.isAuthenticated())
  ) {
    subscriptionManager.setAuthenticating(true);
    handleAuthentication();
  }

  if (subscriptionManager.isConnected() !== isConnected) {
    subscriptionManager.setConnected(isConnected);
  }

  return { authenticated, subscribe, unsubscribe };
};
